import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submenu"]

  connect() {
    // Listen for sidebar toggle events
    this.boundHandleSidebarToggle = this.handleSidebarToggle.bind(this)
    document.addEventListener('click', this.boundHandleSidebarToggle)

    // Turbo connects this controller twice per navigation: once for the cached
    // preview it paints immediately, then again for the real response. Loading
    // on the preview doubles every request the menu makes, so skip it — the
    // real render restores the menu a moment later.
    if (document.documentElement.hasAttribute('data-turbo-preview')) return

    // Restore expanded states only if sidebar is not collapsed
    if (!this.isSidebarCollapsed()) {
      // Small delay to ensure DOM is ready
      this.restoreTimeout = setTimeout(() => {
        this.restoreMenuStates()
      }, 100)
    }
  }

  disconnect() {
    if (this.boundHandleSidebarToggle) {
      document.removeEventListener('click', this.boundHandleSidebarToggle)
    }
    if (this.restoreTimeout) {
      clearTimeout(this.restoreTimeout)
      this.restoreTimeout = null
    }
  }

  handleSidebarToggle(event) {
    // Check if the click was on the sidebar toggle button
    const toggleButton = event.target.closest('[data-action*="sidebar#toggle"]')
    if (toggleButton) {
      // Small delay to let the sidebar animation complete
      setTimeout(() => {
        if (this.isSidebarCollapsed()) {
          this.collapseAllMenus()
        }
      }, 50)
    }
  }

  collapseAllMenus() {
    // Find all expanded submenus and collapse them
    const allButtons = this.element.querySelectorAll('[data-menu-id]')
    allButtons.forEach(button => {
      const menuId = button.dataset.menuId
      let submenu = button.nextElementSibling
      
      if (!submenu || submenu.tagName !== 'DIV') {
        const parent = button.parentElement
        if (parent && parent.nextElementSibling && parent.nextElementSibling.tagName === 'DIV') {
          submenu = parent.nextElementSibling
        }
      }
      
      if (submenu && submenu.classList && !submenu.classList.contains('hidden')) {
        submenu.classList.add('hidden')
        const icon = button.querySelector('[data-icon="chevron"]')
        if (icon) icon.classList.remove('rotate-90')
        this.saveMenuState(menuId, false)
      }
    })
    
    // Also ensure all submenu targets are hidden
    if (this.hasSubmenuTarget) {
      this.submenuTargets.forEach(submenu => {
        if (!submenu.classList.contains('hidden')) {
          submenu.classList.add('hidden')
        }
      })
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const menuId = button.dataset.menuId
    
    // Check if sidebar is collapsed - if so, expand it instead of toggling menu
    if (this.isSidebarCollapsed()) {
      this.expandSidebar()
      return
    }
    
    // Find the submenu - check next sibling first
    let submenu = button.nextElementSibling
    
    // If next sibling doesn't exist or isn't a div, check if button is in a wrapper
    // (this is for dynamically created expand buttons inside wrapper divs)
    if (!submenu || submenu.tagName !== 'DIV') {
      const parent = button.parentElement
      if (parent && parent.nextElementSibling && parent.nextElementSibling.tagName === 'DIV') {
        submenu = parent.nextElementSibling
      }
    }
    
    const icon = button.querySelector('[data-icon="chevron"]')
    
    // Check if submenu is valid
    if (!submenu || !submenu.classList) {
      return
    }

    if (submenu.classList.contains('hidden')) {
      // Expand
      submenu.classList.remove('hidden')
      if (icon) icon.classList.add('rotate-90')
      this.saveMenuState(menuId, true)
      
      // Load dynamic content if needed
      if (button.dataset.loadDynamic === 'true' && submenu.dataset.loaded !== 'true') {
        this.loadDynamicContent(button, submenu)
      }
    } else {
      // Collapse
      submenu.classList.add('hidden')
      if (icon) icon.classList.remove('rotate-90')
      this.saveMenuState(menuId, false)
    }
  }

  async loadDynamicContent(button, submenu) {
    // Guard against a second load landing while the first is still in flight —
    // dataset.loaded is only set once the response arrives.
    if (submenu.dataset.loading === 'true') return
    submenu.dataset.loading = 'true'

    const contentType = button.dataset.contentType
    const parentId = button.dataset.parentId
    const loadingEl = submenu.querySelector('[data-loading]')

    if (loadingEl) loadingEl.classList.remove('hidden')

    try {
      let url
      if (contentType === 'domains') {
        // Ask for the tables of the domains we are about to re-expand in the
        // same round trip, so restoring the menu costs one request, not 1 + N.
        url = `/api/nav/tree${this.expandedDomainsQuery()}`
      } else if (contentType === 'tables') {
        url = `/api/nav/domains/${parentId}/tables`
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const data = await response.json()

      // Remove loading indicator
      if (loadingEl) loadingEl.remove()

      // Render the content
      if (contentType === 'domains') {
        this.renderDomains(submenu, data.domains)
      } else if (contentType === 'tables') {
        this.renderTables(submenu, data.tables)
      }

      submenu.dataset.loaded = 'true'
    } catch (error) {
      console.error('Error loading menu content:', error)
      if (loadingEl) {
        loadingEl.textContent = 'Error loading content'
        loadingEl.classList.remove('hidden')
      }
    } finally {
      delete submenu.dataset.loading
    }
  }

  // The `domain-<id>` entries of the saved state, as an `expanded[]` query string.
  expandedDomainsQuery() {
    const savedState = this.getMenuState()
    const params = Object.keys(savedState)
      .filter(menuId => savedState[menuId] && menuId.startsWith('domain-'))
      .map(menuId => `expanded[]=${encodeURIComponent(menuId.slice('domain-'.length))}`)

    return params.length ? `?${params.join('&')}` : ''
  }

  renderDomains(container, domains) {
    const ul = document.createElement('ul')
    ul.className = 'space-y-1'
    
    // Check if sidebar is collapsed
    const sidebarCollapsed = this.isSidebarCollapsed()
    
    // Load saved menu state to restore expanded domains
    const savedState = this.getMenuState()

    domains.forEach(domain => {
      const li = document.createElement('li')
      
      // Create wrapper div with flex layout
      const wrapper = document.createElement('div')
      wrapper.className = 'flex items-center'
      
      // Create expandable domain button
      const expandButton = document.createElement('button')
      const expandBtnClasses = 'p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors'
      expandButton.className = sidebarCollapsed ? `${expandBtnClasses} hidden` : expandBtnClasses
      expandButton.dataset.action = 'click->hierarchical-menu#toggle'
      expandButton.dataset.menuId = `domain-${domain.id}`
      expandButton.dataset.loadDynamic = 'true'
      expandButton.dataset.contentType = 'tables'
      expandButton.dataset.parentId = domain.id
      expandButton.dataset.sidebarTarget = 'linkText'
      
      expandButton.innerHTML = `
        <svg data-icon="chevron" class="w-3 h-3 transition-transform text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
      `
      
      // Create link to tables filtered by domain
      const link = document.createElement('a')
      link.href = `/tables?my_domains[]=${encodeURIComponent(domain.name)}`
      link.className = 'flex-1 flex items-center gap-x-2 px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors'
      link.innerHTML = `
        <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
        </svg>
        <span class="font-semibold" data-sidebar-target="linkText">${this.escapeHtml(domain.name)}</span>
      `
      
      wrapper.appendChild(expandButton)
      wrapper.appendChild(link)
      
      // Create submenu for tables
      const submenu = document.createElement('div')
      submenu.className = 'hidden ml-7 mt-1 space-y-1'
      submenu.innerHTML = '<div data-loading class="px-3 py-2 text-xs text-gray-500">Loading...</div>'
      
      li.appendChild(wrapper)
      li.appendChild(submenu)
      ul.appendChild(li)
      
      // Check if this domain was expanded in saved state and restore it
      const domainMenuId = `domain-${domain.id}`
      if (savedState[domainMenuId] && !sidebarCollapsed) {
        submenu.classList.remove('hidden')
        const icon = expandButton.querySelector('[data-icon="chevron"]')
        if (icon) icon.classList.add('rotate-90')

        if (Array.isArray(domain.tables)) {
          // Tables came back with the tree request — no second round trip.
          this.renderTables(submenu, domain.tables)
          submenu.dataset.loaded = 'true'
        } else {
          // Use setTimeout to allow DOM to update
          setTimeout(() => {
            if (submenu.dataset.loaded !== 'true') {
              this.loadDynamicContent(expandButton, submenu)
            }
          }, 50)
        }
      }
    })

    container.appendChild(ul)
  }

  renderTables(container, tables) {
    const ul = document.createElement('ul')
    ul.className = 'space-y-1'

    tables.forEach(table => {
      const li = document.createElement('li')
      const link = document.createElement('a')
      link.href = `/tables/${table.id}`
      link.className = 'flex items-center gap-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors'
      link.innerHTML = `
      <svg class="w-4 h-4 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke-width="2"/>
        <path d="M3 9h18" stroke-width="2"/>
      </svg>
        <span class="flex-1 font-semibold" data-sidebar-target="linkText">${this.escapeHtml(table.name)}</span>
      `
      li.appendChild(link)
      ul.appendChild(li)
    })

    // Clear existing content and add new list
    const loadingIndicator = container.querySelector('[data-loading]')
    if (loadingIndicator) loadingIndicator.remove()
    container.appendChild(ul)
  }

  getMenuState() {
    try {
      const saved = sessionStorage.getItem('hierarchicalMenuState')
      return saved ? JSON.parse(saved) : {}
    } catch (e) {
      return {}
    }
  }

  saveMenuState(menuId, isExpanded) {
    const currentState = this.getMenuState()
    currentState[menuId] = isExpanded
    sessionStorage.setItem('hierarchicalMenuState', JSON.stringify(currentState))
  }

  restoreMenuStates() {
    const menuState = this.getMenuState()
    
    if (!menuState || Object.keys(menuState).length === 0) {
      return
    }
    
    Object.keys(menuState).forEach(menuId => {
      if (menuState[menuId]) {
        const button = this.element.querySelector(`[data-menu-id="${menuId}"]`)
        
        if (button) {
          // Find the submenu - check next sibling first
          let submenu = button.nextElementSibling
          
          // If next sibling doesn't exist or isn't a div, check if button is in a wrapper
          // (this is for dynamically created expand buttons inside wrapper divs)
          if (!submenu || submenu.tagName !== 'DIV') {
            const parent = button.parentElement
            if (parent && parent.nextElementSibling && parent.nextElementSibling.tagName === 'DIV') {
              submenu = parent.nextElementSibling
            }
          }
          
          const icon = button.querySelector('[data-icon="chevron"]')
          if (submenu && submenu.classList) {
            submenu.classList.remove('hidden')
            if (icon) icon.classList.add('rotate-90')
            
            // Reload dynamic content if needed
            if (button.dataset.loadDynamic === 'true' && submenu.dataset.loaded !== 'true') {
              this.loadDynamicContent(button, submenu)
            }
          }
        }
      }
    })
  }

  isSidebarCollapsed() {
    // Check if the sidebar is in collapsed state by looking for the width class
    const sidebarElement = document.querySelector('[data-sidebar-target="sidebar"]')
    return sidebarElement && sidebarElement.classList.contains('md:w-[57px]')
  }

  expandSidebar() {
    // Find and click the sidebar toggle button to expand it
    const toggleButton = document.querySelector('[data-action*="sidebar#toggle"]')
    if (toggleButton) {
      toggleButton.click()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
